import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { DataGrid, GridColDef, GridRowsProp, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Paper from 'components/@extended/Paper'

dayjs.extend(utc)
import { DATE_FORMAT } from 'types/periods'
import { PlusOutlined, DeleteOutlined, DownOutlined, UpOutlined, EditOutlined } from '@ant-design/icons'
import type { PrepareLedgerItem } from 'types/accruals'

interface DebitCreditBalanceTableProps {
  lineItems: PrepareLedgerItem[]
  adjustments: Record<string, AdjustmentValue>
  notes: Record<string, string>
  onAddLineItem: (baseItem: PrepareLedgerItem, adjustmentAmount: number, lineItemLabel: string, notes?: string) => void
  onDeleteLineItem?: (item: PrepareLedgerItem) => void
  onUpdateNotes: (compositeId: string, notes: string) => void
  periodIdToLabel: Record<string, string>
  isLoading?: boolean
}

interface UserLineItems {
  userKey: string
  userName: string
  items: PrepareLedgerItem[]
  currentBalance: number
}

interface AdjustmentValue {
  percentChange: number
  fixedChange: number
  activeType: 'percentage' | 'currency'
  currentBalance: number
  originalBalance: number
}

interface AdjustmentDialogProps {
  open: boolean
  userLineItems: UserLineItems | null
  periodIdToLabel: Record<string, string>
  adjustments: Record<string, AdjustmentValue>
  getCompositeId: (item: PrepareLedgerItem) => string
  onClose: () => void
  onSave: (adjustmentAmount: number, lineItemLabel: string, notes?: string) => void
}

interface DataGridRow {
  id: string
  type: 'user' | 'item'
  userId?: string
  firstName?: string
  lastName?: string
  lineItemName?: string
  debit: number
  credit: number
  notes?: string
  parentId?: string
  level: number
  originalItem?: PrepareLedgerItem
  userKey?: string
  canAddAdjustment?: boolean
  canEdit?: boolean
  canDelete?: boolean
}

// Global state to persist dialog data across navigation (simple solution)
const dialogStateCache = new Map<
  string,
  {
    pendingAdjustments: Array<{
      id: string
      type: 'debit' | 'credit'
      amount: number
      label: string
      notes?: string
      date: dayjs.Dayjs
    }>
    formData: {
      adjustmentType: 'debit' | 'credit'
      amountType: 'percentage' | 'value'
      percentageBase: 'change' | 'balance'
      amount: string
      lineItemLabel: string
      notes: string
      adjustmentDate: dayjs.Dayjs
    }
  }
>()

const AdjustmentDialog: FC<AdjustmentDialogProps> = ({
  open,
  userLineItems,
  periodIdToLabel,
  adjustments,
  getCompositeId,
  onClose,
  onSave,
}) => {
  const theme = useTheme()

  // Initialize form data from cache or defaults
  const cacheKey = userLineItems?.userKey || ''
  const cachedData = dialogStateCache.get(cacheKey)

  const [adjustmentType, setAdjustmentType] = useState<'debit' | 'credit'>(
    cachedData?.formData.adjustmentType || 'credit'
  )
  const [amountType, setAmountType] = useState<'percentage' | 'value'>(cachedData?.formData.amountType || 'value')
  const [percentageBase, setPercentageBase] = useState<'change' | 'balance'>(
    cachedData?.formData.percentageBase || 'change'
  )
  const [amount, setAmount] = useState(cachedData?.formData.amount || '')
  const [lineItemLabel, setLineItemLabel] = useState(cachedData?.formData.lineItemLabel || '')
  const [notes, setNotes] = useState(cachedData?.formData.notes || '')
  const [adjustmentDate, setAdjustmentDate] = useState(cachedData?.formData.adjustmentDate || dayjs.utc())

  const [pendingAdjustments, setPendingAdjustments] = useState<
    Array<{
      id: string
      type: 'debit' | 'credit'
      amount: number
      label: string
      notes?: string
      date: dayjs.Dayjs
    }>
  >(cachedData?.pendingAdjustments || [])

  // Update cache whenever form data changes
  useEffect(() => {
    if (cacheKey) {
      dialogStateCache.set(cacheKey, {
        pendingAdjustments,
        formData: {
          adjustmentType,
          amountType,
          percentageBase,
          amount,
          lineItemLabel,
          notes,
          adjustmentDate,
        },
      })
    }
  }, [
    cacheKey,
    pendingAdjustments,
    adjustmentType,
    amountType,
    percentageBase,
    amount,
    lineItemLabel,
    notes,
    adjustmentDate,
  ])

  const addAdjustment = () => {
    if (!lineItemLabel.trim() || !amount.trim() || parseFloat(amount) <= 0) return

    let finalAmount = parseFloat(amount)

    // Calculate percentage-based amount
    if (amountType === 'percentage' && userLineItems) {
      let baseAmount = 0

      if (percentageBase === 'change') {
        // Calculate against the change (credit sum)
        baseAmount = userLineItems.items.reduce((sum, item) => {
          const compositeId = getCompositeId(item)
          const adjustment = adjustments[compositeId]

          let balanceChange = 0
          if (adjustment) {
            balanceChange = adjustment.currentBalance - item.previousBalance
          } else {
            balanceChange = item.balance - item.previousBalance
          }

          return sum + (balanceChange > 0 ? balanceChange : 0)
        }, 0)
      } else {
        // Calculate against running balance (current balance)
        baseAmount = userLineItems.items.reduce((sum, item) => {
          const compositeId = getCompositeId(item)
          const adjustment = adjustments[compositeId]

          const currentBalance = adjustment ? adjustment.currentBalance : item.balance
          return sum + (currentBalance > 0 ? currentBalance : 0)
        }, 0)
      }

      finalAmount = baseAmount * (parseFloat(amount) / 100)
    }

    const newAdjustment = {
      id: `${Date.now()}-${Math.random()}`,
      type: adjustmentType,
      amount: finalAmount,
      label: lineItemLabel.trim(),
      notes: notes.trim() || undefined,
      date: adjustmentDate,
    }

    setPendingAdjustments((prev) => [...prev, newAdjustment])
    setAmount('')
    setLineItemLabel('')
    setNotes('')
  }

  const removeAdjustment = (id: string) => {
    setPendingAdjustments((prev) => prev.filter((adj) => adj.id !== id))
  }

  const applyAllAdjustments = () => {
    pendingAdjustments.forEach((adj) => {
      const adjustmentAmount = adj.type === 'credit' ? adj.amount : -adj.amount
      onSave(adjustmentAmount, adj.label, adj.notes)
    })

    // Clear cache and reset state
    dialogStateCache.delete(cacheKey)
    setPendingAdjustments([])
    setAdjustmentType('credit')
    setAmountType('value')
    setPercentageBase('change')
    setAmount('')
    setLineItemLabel('')
    setNotes('')
    setAdjustmentDate(dayjs())

    onClose()
  }

  const handleClose = () => {
    onClose()
  }

  const getTotalDebitsWithPending = useMemo(() => {
    if (!userLineItems) return 0

    const existingDebits = userLineItems.items.reduce((sum, item) => {
      const compositeId = getCompositeId(item)
      const adjustment = adjustments[compositeId]
      let balanceChange = adjustment
        ? adjustment.currentBalance - item.previousBalance
        : item.balance - item.previousBalance
      return sum + (balanceChange < 0 ? Math.abs(balanceChange) : 0)
    }, 0)

    const pendingDebits = pendingAdjustments
      .filter((adj) => adj.type === 'debit')
      .reduce((sum, adj) => sum + adj.amount, 0)

    return existingDebits + pendingDebits
  }, [userLineItems, adjustments, getCompositeId, pendingAdjustments])

  const getTotalCreditsWithPending = useMemo(() => {
    if (!userLineItems) return 0

    const existingCredits = userLineItems.items.reduce((sum, item) => {
      const compositeId = getCompositeId(item)
      const adjustment = adjustments[compositeId]
      let balanceChange = adjustment
        ? adjustment.currentBalance - item.previousBalance
        : item.balance - item.previousBalance
      return sum + (balanceChange > 0 ? balanceChange : 0)
    }, 0)

    const pendingCredits = pendingAdjustments
      .filter((adj) => adj.type === 'credit')
      .reduce((sum, adj) => sum + adj.amount, 0)

    return existingCredits + pendingCredits
  }, [userLineItems, adjustments, getCompositeId, pendingAdjustments])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Adjust Line Items for {userLineItems?.userName}</Typography>
      </DialogTitle>
      <DialogContent>
        {userLineItems && (
          <Box sx={{ display: 'flex', gap: 3, height: 500 }}>
            {/* Left Column - Table */}
            <Box sx={{ flex: 1 }}>
              <Paper sx={{ height: '100%' }}>
                <TableContainer sx={{ height: '100%' }}>
                  <Table stickyHeader size="small">
                    <TableHead
                      sx={{
                        '& th': {
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderBottom: `2px solid ${theme.palette.divider} !important`,
                        },
                      }}
                    >
                      <TableRow>
                        <TableCell sx={{ minWidth: 300 }}>User / Line Item</TableCell>
                        <TableCell align="right" sx={{ width: 120 }}>
                          Debit
                        </TableCell>
                        <TableCell align="right" sx={{ width: 120 }}>
                          Credit
                        </TableCell>
                        <TableCell align="center" sx={{ width: 120 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* User header row */}
                      <TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            {userLineItems.items[0]?.firstName} {userLineItems.items[0]?.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {getTotalDebitsWithPending > 0 ? (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.875rem' }}
                            >
                              ${getTotalDebitsWithPending.toFixed(2)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {getTotalCreditsWithPending > 0 ? (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.875rem' }}
                            >
                              ${getTotalCreditsWithPending.toFixed(2)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>

                      {/* Existing line items */}
                      {userLineItems.items.map((item) => {
                        const compositeId = getCompositeId(item)
                        const adjustment = adjustments[compositeId]
                        let balanceChange = adjustment
                          ? adjustment.currentBalance - item.previousBalance
                          : item.balance - item.previousBalance

                        const lineItemName = `${periodIdToLabel[item.periodId] || 'Unknown Period'}${item.allocationTableName ? ` | ${item.allocationTableName}` : ''} | ${item.allocationRowName}`
                        const debit = balanceChange < 0 ? Math.abs(balanceChange) : 0
                        const credit = balanceChange > 0 ? balanceChange : 0

                        return (
                          <TableRow key={compositeId}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', pl: 4 }}>
                                {lineItemName}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {debit > 0 ? (
                                <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                                  ${debit.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {credit > 0 ? (
                                <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                                  ${credit.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center"></TableCell>
                          </TableRow>
                        )
                      })}

                      {/* Pending adjustments */}
                      {pendingAdjustments.map((adj) => (
                        <TableRow key={adj.id} sx={{ backgroundColor: theme.palette.action.hover }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', pl: 4, fontStyle: 'italic' }}>
                              {userLineItems.items[0]?.periodId
                                ? periodIdToLabel[userLineItems.items[0].periodId] || 'Unknown Period'
                                : 'Unknown Period'}
                              {userLineItems.items[0]?.allocationTableName
                                ? ` | ${userLineItems.items[0].allocationTableName}`
                                : ''}{' '}
                              | {adj.label} (Pending)
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {adj.type === 'debit' ? (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.875rem' }}
                              >
                                ${adj.amount.toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {adj.type === 'credit' ? (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.875rem' }}
                              >
                                ${adj.amount.toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => removeAdjustment(adj.id)} color="error">
                              <DeleteOutlined />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>

            {/* Right Column - Adjustment Controls */}
            <Box sx={{ width: 350 }}>
              {/* Adjustment Form */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add Adjustment
                </Typography>

                <ToggleButtonGroup
                  value={adjustmentType}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setAdjustmentType(newValue)
                    }
                  }}
                  aria-label="adjustment type"
                  size="small"
                  sx={{ mb: 2, width: '100%' }}
                >
                  <ToggleButton value="debit" aria-label="Debit" sx={{ width: '50%' }}>
                    <Typography>Debit</Typography>
                  </ToggleButton>
                  <ToggleButton value="credit" aria-label="Credit" sx={{ width: '50%' }}>
                    <Typography>Credit</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  value={amountType}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setAmountType(newValue)
                    }
                  }}
                  aria-label="amount type"
                  size="small"
                  sx={{ mb: 2, width: '100%' }}
                >
                  <ToggleButton value="percentage" aria-label="Percentage" sx={{ width: '50%' }}>
                    <Typography>Percentage</Typography>
                  </ToggleButton>
                  <ToggleButton value="value" aria-label="Fixed Value" sx={{ width: '50%' }}>
                    <Typography>Fixed Value</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>

                {amountType === 'percentage' && (
                  <ToggleButtonGroup
                    value={percentageBase}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        setPercentageBase(newValue)
                      }
                    }}
                    aria-label="percentage base"
                    size="small"
                    sx={{ mb: 2, width: '100%' }}
                  >
                    <ToggleButton value="change" aria-label="Against Change" sx={{ width: '50%' }}>
                      <Typography variant="caption">vs Change</Typography>
                    </ToggleButton>
                    <ToggleButton value="balance" aria-label="Against Balance" sx={{ width: '50%' }}>
                      <Typography variant="caption">vs Balance</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}

                <TextField
                  label="Line Item Label"
                  value={lineItemLabel}
                  onChange={(e) => setLineItemLabel(e.target.value)}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      '&.MuiInputLabel-shrink': {
                        background: theme.palette.background.paper,
                      },
                    },
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '21px',
                    },
                  }}
                  placeholder="Enter a description for this adjustment..."
                  required
                />

                <TextField
                  label={amountType === 'percentage' ? 'Percentage (%)' : 'Adjustment Amount ($)'}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      '&.MuiInputLabel-shrink': {
                        background: theme.palette.background.paper,
                      },
                    },
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '21px',
                    },
                  }}
                  inputProps={{ min: 0, step: amountType === 'percentage' ? 1 : 0.01 }}
                />

                <TextField
                  label="Notes (Optional)"
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      '&.MuiInputLabel-shrink': {
                        background: theme.palette.background.paper,
                      },
                    },
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '21px',
                    },
                  }}
                  placeholder="Add notes for this adjustment..."
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Adjustment Date"
                    value={adjustmentDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        const dateString = newValue.format('YYYY-MM-DD')
                        const utcDate = dayjs.utc(dateString)
                        setAdjustmentDate(utcDate)
                      } else {
                        setAdjustmentDate(dayjs.utc())
                      }
                    }}
                    format={DATE_FORMAT}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        InputProps: {
                          readOnly: true,
                        },
                        InputLabelProps: {
                          shrink: true,
                          sx: {
                            '&.MuiInputLabel-shrink': {
                              background: theme.palette.background.paper,
                            },
                          },
                        },
                        sx: {
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '21px',
                          },
                        },
                        helperText: 'Click calendar icon to select date',
                      },
                    }}
                  />
                </LocalizationProvider>

                <Button
                  variant="contained"
                  onClick={addAdjustment}
                  disabled={!lineItemLabel.trim() || !amount.trim() || parseFloat(amount) <= 0}
                  fullWidth
                >
                  Add to Pending Adjustments
                </Button>
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={applyAllAdjustments} variant="contained" disabled={pendingAdjustments.length === 0}>
          Apply {pendingAdjustments.length} Adjustment{pendingAdjustments.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface NotesDialogProps {
  open: boolean
  item: PrepareLedgerItem | null
  currentNotes: string
  onClose: () => void
  onSave: (notes: string) => void
}

const NotesDialog: FC<NotesDialogProps> = ({ open, item, currentNotes, onClose, onSave }) => {
  const theme = useTheme()
  const [notes, setNotes] = useState('')

  useEffect(() => {
    setNotes(currentNotes || '')
  }, [currentNotes, open])

  const handleSave = () => {
    onSave(notes)
    onClose()
  }

  const handleClose = () => {
    setNotes(currentNotes || '')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Edit Notes</Typography>
      </DialogTitle>
      <DialogContent>
        {item && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {item.firstName} {item.lastName} - {item.allocationTableName}
              {item.allocationTableName ? ' | ' : ''}
              {item.allocationRowName}
            </Typography>
            <TextField
              label="Notes"
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
                sx: {
                  '&.MuiInputLabel-shrink': {
                    background: theme.palette.background.paper,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
              placeholder="Add notes for this line item..."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Notes
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const DebitCreditBalanceTable: FC<DebitCreditBalanceTableProps> = ({
  lineItems,
  adjustments,
  notes,
  onAddLineItem,
  onDeleteLineItem,
  onUpdateNotes,
  periodIdToLabel,
  isLoading = false,
}) => {
  const theme = useTheme()
  const [adjustmentDialog, setAdjustmentDialog] = useState<{
    open: boolean
    userLineItems: UserLineItems | null
  }>({ open: false, userLineItems: null })
  const [notesDialog, setNotesDialog] = useState<{
    open: boolean
    item: PrepareLedgerItem | null
  }>({ open: false, item: null })
  const [isExpanded, setIsExpanded] = useState<boolean>(true)
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'lastName', sort: 'asc' }])

  const getCompositeId = useCallback(
    (item: PrepareLedgerItem) => `${item.allocationTableId}-${item.allocationRowId}`,
    []
  )

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const getUserDebitTotal = useCallback(
    (userKey: string, userItems: PrepareLedgerItem[]) => {
      return userItems.reduce((sum, item) => {
        const compositeId = getCompositeId(item)
        const adjustment = adjustments[compositeId]
        let balanceChange = adjustment
          ? adjustment.currentBalance - item.previousBalance
          : item.balance - item.previousBalance
        return sum + (balanceChange < 0 ? Math.abs(balanceChange) : 0)
      }, 0)
    },
    [adjustments, getCompositeId]
  )

  const getUserCreditTotal = useCallback(
    (userKey: string, userItems: PrepareLedgerItem[]) => {
      return userItems.reduce((sum, item) => {
        const compositeId = getCompositeId(item)
        const adjustment = adjustments[compositeId]
        let balanceChange = adjustment
          ? adjustment.currentBalance - item.previousBalance
          : item.balance - item.previousBalance
        return sum + (balanceChange > 0 ? balanceChange : 0)
      }, 0)
    },
    [adjustments, getCompositeId]
  )

  // Prepare data for DataGrid with custom sorting
  const rows: GridRowsProp<DataGridRow> = useMemo(() => {
    const userGroups: Record<string, PrepareLedgerItem[]> = {}

    lineItems.forEach((item) => {
      const userKey = `${item.userId}-${item.firstName}-${item.lastName}`
      if (!userGroups[userKey]) {
        userGroups[userKey] = []
      }
      userGroups[userKey].push(item)
    })

    // Create user data with totals for sorting
    const userEntries = Object.entries(userGroups).map(([userKey, userItems]) => {
      const user = userItems[0]
      const userDebitTotal = getUserDebitTotal(userKey, userItems)
      const userCreditTotal = getUserCreditTotal(userKey, userItems)

      return {
        userKey,
        userItems,
        user,
        userDebitTotal,
        userCreditTotal,
      }
    })

    // Apply sorting to users only
    if (sortModel.length > 0) {
      const sortField = sortModel[0].field
      const sortDirection = sortModel[0].sort

      userEntries.sort((a, b) => {
        let aValue: any, bValue: any

        switch (sortField) {
          case 'lastName':
            aValue = a.user.lastName
            bValue = b.user.lastName
            break
          case 'firstName':
            aValue = a.user.firstName
            bValue = b.user.firstName
            break
          case 'debit':
            aValue = a.userDebitTotal
            bValue = b.userDebitTotal
            break
          case 'credit':
            aValue = a.userCreditTotal
            bValue = b.userCreditTotal
            break
          case 'lineItemName':
            aValue = `${a.user.firstName} ${a.user.lastName}`
            bValue = `${b.user.firstName} ${b.user.lastName}`
            break
          default:
            aValue = a.user.lastName
            bValue = b.user.lastName
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const result = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? result : -result
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          const result = aValue - bValue
          return sortDirection === 'asc' ? result : -result
        }

        return 0
      })
    }

    // Build final rows maintaining user groupings
    const result: DataGridRow[] = []

    userEntries.forEach(({ userKey, userItems, user, userDebitTotal, userCreditTotal }) => {
      // Add user header row
      result.push({
        id: userKey,
        type: 'user',
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        debit: userDebitTotal,
        credit: userCreditTotal,
        level: 0,
        userKey,
        canAddAdjustment: true,
      })

      // Add line items if expanded
      if (isExpanded) {
        userItems.forEach((item) => {
          const compositeId = getCompositeId(item)
          const adjustment = adjustments[compositeId]
          let balanceChange = adjustment
            ? adjustment.currentBalance - item.previousBalance
            : item.balance - item.previousBalance

          const lineItemName = `${periodIdToLabel[item.periodId] || 'Unknown Period'}${item.allocationTableName ? ` | ${item.allocationTableName}` : ''} | ${item.allocationRowName}`

          result.push({
            id: compositeId,
            type: 'item',
            parentId: userKey,
            lineItemName,
            debit: balanceChange < 0 ? Math.abs(balanceChange) : 0,
            credit: balanceChange > 0 ? balanceChange : 0,
            notes: notes[compositeId],
            level: 1,
            originalItem: item,
            canEdit: true,
            canDelete: !!onDeleteLineItem,
          })
        })
      }
    })

    return result
  }, [
    lineItems,
    getUserDebitTotal,
    getUserCreditTotal,
    isExpanded,
    adjustments,
    getCompositeId,
    periodIdToLabel,
    notes,
    onDeleteLineItem,
    sortModel,
  ])

  const columns: GridColDef<DataGridRow>[] = [
    {
      field: 'lineItemName',
      headerName: 'User / Line Item',
      flex: 1,
      minWidth: 400,
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        if (params.row.type === 'user') {
          return (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                pl: params.row.level * 2,
                fontSize: '0.875rem',
              }}
            >
              {params.row.firstName} {params.row.lastName}
            </Typography>
          )
        }
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              pl: params.row.level * 4,
            }}
          >
            {params.row.lineItemName}
          </Typography>
        )
      },
    },
    {
      field: 'debit',
      headerName: 'Debit',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        const debit = params.row.debit
        if (debit > 0) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: params.row.type === 'user' ? 'bold' : 'normal',
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              ${debit.toFixed(2)}
            </Typography>
          )
        }
        return (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            -
          </Typography>
        )
      },
    },
    {
      field: 'credit',
      headerName: 'Credit',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        const credit = params.row.credit
        if (credit > 0) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: params.row.type === 'user' ? 'bold' : 'normal',
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              ${credit.toFixed(2)}
            </Typography>
          )
        }
        return (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            -
          </Typography>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography>Actions</Typography>
          <IconButton size="small" onClick={toggleExpanded}>
            {isExpanded ? <UpOutlined /> : <DownOutlined />}
          </IconButton>
        </Box>
      ),
      renderCell: (params: GridRenderCellParams<DataGridRow>) => {
        if (params.row.type === 'user' && params.row.canAddAdjustment) {
          const userItems = lineItems.filter(
            (item) => `${item.userId}-${item.firstName}-${item.lastName}` === params.row.userKey
          )

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PlusOutlined />}
                onClick={() => {
                  const userLineItems: UserLineItems = {
                    userKey: params.row.userKey!,
                    userName: `${params.row.firstName} ${params.row.lastName}`,
                    items: userItems,
                    currentBalance: params.row.credit - params.row.debit,
                  }
                  setAdjustmentDialog({ open: true, userLineItems })
                }}
              >
                Adjustment
              </Button>
            </Box>
          )
        }

        if (params.row.type === 'item') {
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {params.row.canEdit && (
                <IconButton
                  size="small"
                  onClick={() => setNotesDialog({ open: true, item: params.row.originalItem! })}
                  color="primary"
                  title="Edit notes"
                >
                  <EditOutlined />
                </IconButton>
              )}
              {params.row.canDelete && onDeleteLineItem && (
                <IconButton
                  size="small"
                  onClick={() => onDeleteLineItem(params.row.originalItem!)}
                  color="error"
                  title="Delete item"
                >
                  <DeleteOutlined />
                </IconButton>
              )}
            </Box>
          )
        }

        return null
      },
    },
  ]

  const handleAdjustmentSave = (adjustmentAmount: number, lineItemLabel: string, adjustmentNotes?: string) => {
    if (adjustmentDialog.userLineItems) {
      const baseItem = adjustmentDialog.userLineItems.items[0]
      onAddLineItem(baseItem, adjustmentAmount, lineItemLabel, adjustmentNotes)
    }
  }

  const handleNotesSave = (newNotes: string) => {
    if (notesDialog.item) {
      const compositeId = getCompositeId(notesDialog.item)
      onUpdateNotes(compositeId, newNotes)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography>Loading...</Typography>
      </Box>
    )
  }

  return (
    <>
      <Paper sx={{ height: '100%', width: '100%', minHeight: 300, display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          disableColumnSelector
          disableColumnMenu
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          sortingMode="server"
          density="standard"
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          pageSizeOptions={[50, 100, 200]}
          getRowClassName={(params) => {
            const row = params.row as DataGridRow
            if (row.type === 'user') {
              const userIndex = rows.filter((r) => r.type === 'user').findIndex((r) => r.id === row.id)
              return userIndex % 2 === 0 ? 'user-row-even' : 'user-row-odd'
            } else {
              const parentRow = rows.find((r) => r.id === row.parentId)
              if (parentRow) {
                const userIndex = rows.filter((r) => r.type === 'user').findIndex((r) => r.id === parentRow.id)
                return userIndex % 2 === 0 ? 'item-row-even' : 'item-row-odd'
              }
              return 'item-row-even'
            }
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              borderTop: `1px solid ${theme.palette.divider}`,
              borderBottom: `2px solid ${theme.palette.divider} !important`,
              backgroundColor: theme.palette.background.default,
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              userSelect: 'none',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: `${theme.palette.action.hover} !important`,
              },
              '&.user-row-even, &.item-row-even': {
                backgroundColor: theme.palette.background.paper,
              },
              '&.user-row-odd, &.item-row-odd': {
                backgroundColor: theme.palette.action.hover,
              },
            },
            '& .MuiDataGrid-cell': {
              paddingTop: '12px',
              paddingBottom: '12px',
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
          }}
        />
      </Paper>

      <AdjustmentDialog
        open={adjustmentDialog.open}
        userLineItems={adjustmentDialog.userLineItems}
        periodIdToLabel={periodIdToLabel}
        adjustments={adjustments}
        getCompositeId={getCompositeId}
        onClose={() => setAdjustmentDialog({ open: false, userLineItems: null })}
        onSave={handleAdjustmentSave}
      />

      <NotesDialog
        open={notesDialog.open}
        item={notesDialog.item}
        currentNotes={notesDialog.item ? notes[getCompositeId(notesDialog.item)] || '' : ''}
        onClose={() => setNotesDialog({ open: false, item: null })}
        onSave={handleNotesSave}
      />
    </>
  )
}

export default DebitCreditBalanceTable
